import React, { useEffect, useState } from "react";
import { X, Loader2, Settings, Eye, CheckCircle2, PhoneCall, Bell } from "lucide-react";
import toast from "react-hot-toast";

import {
  getProposalNotificationFeed, markProposalNotificationRead, markAllProposalNotificationsRead,
} from "../api/prospectingReportsApi";

const KIND_ICON = {
  viewed: Eye,
  accepted: CheckCircle2,
  callback: PhoneCall,
};
const ago = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/** Proply bell → the notifications feed (views / acceptances / callbacks). */
const NotificationFeedModal = ({ onClose, onOpenSettings, onOpenProposal }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getProposalNotificationFeed()
      .then(({ data }) => setItems(data?.results || []))
      .catch(() => toast.error("Could not load notifications."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const open = async (n) => {
    try { if (!n.is_read) await markProposalNotificationRead(n.id); } catch (e) { /* non-fatal */ }
    if (onOpenProposal && n.proposal) onOpenProposal(n.proposal);
  };
  const markAll = async () => {
    try { await markAllProposalNotificationsRead(); setItems((p) => p.map((n) => ({ ...n, is_read: true }))); }
    catch (e) { toast.error("Could not update."); }
  };

  const unread = items.filter((n) => !n.is_read).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 pt-20">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[75vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5" style={{ color: "#0f8d9b" }} /> Notifications
            {unread > 0 && <span className="text-xs font-bold text-white bg-[#0f8d9b] rounded-full px-2 py-0.5">{unread}</span>}
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={onOpenSettings} className="text-gray-400 hover:text-[#0f8d9b]" title="Notification settings"><Settings className="w-5 h-5" /></button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="px-5 py-2 border-b border-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-400">{items.length} recent</span>
          <button onClick={markAll} disabled={!unread} className="text-xs font-semibold text-[#0f8d9b] disabled:text-gray-300">Mark all as read</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">No notifications yet.</div>
          ) : (
            items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <button key={n.id} onClick={() => open(n)}
                  className={`w-full text-left flex gap-3 px-5 py-3 border-b border-gray-50 hover:bg-gray-50 ${n.is_read ? "" : "bg-[#eaf7f8]/40"}`}>
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#0f8d9b" }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800">{n.message || n.kind}</p>
                    <p className="text-[11px] text-gray-400">{n.proposal_address ? `${n.proposal_address} · ` : ""}{ago(n.created_at)}</p>
                  </div>
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#0f8d9b] mt-1.5 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationFeedModal;
